import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import LoadingPage from "../../../LoadingPage";
import Table, { Column, DataSourceItem } from "../../../../Components/Atoms/Table";
import TablePaginationFooter from "../../../../Components/Atoms/Table/TablePaginationFooter";
import { useInvoiceStore } from "../../../../Store/Invoice";
import { PAGINATION_KEYS, usePagination } from "../../../../Store/Pagination";
import { useTeamStore } from "../../../../Store/Team";
import {
  Invoice,
  InvoiceStatus,
  PaymentsInvoiceAutoCollectionStatus
} from "../../../../Contracts/Invoice";
import { toLocalDateString } from "../../../../Utils/DateUtils";
import { ReactComponent as ExternalLinkIcon } from "../../../../Assets/ActionIcons/ExternalLinkIcon.svg";
import { createSortFunction } from "../../../../Utils/TableUtils";
import InvoiceListActions from "../../../../Components/Molecules/InvoiceListActions";
import { ReactComponent as ExclamationWarningIcon } from "../../../../Assets/ActionIcons/ExclamationWarningIcon.svg";
import Popover from "../../../../Components/Atoms/Popover";
import Tag from "../../../../Components/Atoms/Tag";
import { useOnboardingStore } from "../../../../Store/Onboarding";
import { getPaymentMethodDetails } from "../../../../Contracts/Transaction";

const CustomerDetailInvoiceList: React.FC = () => {
  const [sortParamValue, setSortParamValue] = useState<string>("invoice_number:desc");
  const [openMoreMenu, setOpenMoreMenu] = useState<number>(-1);
  const { id: customerID } = useParams();
  const { formatCurrency } = useTeamStore();
  const { getAdyenKYCDetails } = useOnboardingStore();

  const { limit, page, setLimit, setPage } = usePagination(
    PAGINATION_KEYS.CUSTOMER_DETAIL_INVOICE_LIST,
    { limit: 25, page: 1 }
  );

  const { isLoading, invoices, invoicesCount, getInvoices, invoiceBaseUrl } = useInvoiceStore();

  const columns: Column<Invoice>[] = [
    {
      title: "Invoice Number",
      dataIndex: "invoice",
      key: "invoice",
      sortClicked: createSortFunction("invoice_number", setSortParamValue),
      width: "w-20",
      defaultSortOrder: "desc",
      render: d => (
        <a
          href={`${invoiceBaseUrl}${d.data.collectible_invoice_id}`}
          className="text-blue-600 flex space-x-2"
          target="_blank"
          rel="noreferrer noopener"
        >
          <div>{d.invoice}</div>
          <ExternalLinkIcon className="w-4 h-4 text-gray-600" />
        </a>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: d => (
        <div className="flex items-center space-x-2">
          {d.data.payment_is_processing ? (
            <Tag color="orange">Processing</Tag>
          ) : (
            <Tag
              color={
                d.data.status === InvoiceStatus.Overdue
                  ? "red"
                  : d.data.status === InvoiceStatus.Paid
                    ? "lime"
                    : d.data.status === InvoiceStatus.Open
                      ? "blue"
                      : "gray"
              }
            >
              {d.data.status}
            </Tag>
          )}
          {d.data.is_invoice_associated_with_chargeback && (
            <Popover
              message={
                <div>
                  The invoice is associated with a chargeback.{" "}
                  <a
                    href="https://help.zomentum.com/support/solutions/articles/44002440952-how-is-a-chargeback-handled-in-my-accounting-software-"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline cursor-pointer"
                  >
                    Learn More
                  </a>
                </div>
              }
            >
              <ExclamationWarningIcon className="w-3 h-3 text-red-600" />
            </Popover>
          )}
        </div>
      ),
      width: "w-24"
    },
    {
      title: "Invoice Amount",
      dataIndex: "amount",
      key: "amount",
      render: d => (
        <span className="underline decoration-dashed decoration-gray-400 underline-offset-4 cursor-pointer relative group">
          {d.total}
          <div className="group-hover:block hidden absolute w-80 top-5 left-0 bg-white shadow-lg p-2 rounded z-40">
            <div className="mb-3">Amount Breakup</div>
            <div className="grid grid-cols-5 border-b">
              <div className="col-span-3 border-y py-2 font-semibold">Type</div>
              <div className="col-span-2 border-y py-2 font-semibold text-right">Amount</div>
              <div className="col-span-3 border-left py-2 border-r">Total Amount</div>
              <div className="col-span-2 py-2 text-right">
                <div>{d.total}</div>
              </div>
              <div className="col-span-3 border-left py-2 border-r">Outstanding Amount</div>
              <div className="col-span-2 py-2 text-right">
                <div>{d.balance}</div>
              </div>
              {d.data.surcharge_amount !== 0 && (
                <>
                  <div className="col-span-3 border-left py-2 border-r">Transaction Fee</div>
                  <div className="col-span-2 py-2 text-right">
                    <div>{d.surcharge_amount}</div>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-end py-3 space-x-2">
              <div className="font-semibold">Amount Paid:</div>
              <div>{d.amount_paid}</div>
            </div>
          </div>
        </span>
      ),
      width: "w-24"
    },
    {
      title: "Date Issued",
      dataIndex: "dateIssued",
      key: "dateIssued",
      sortClicked: createSortFunction("issue_date", setSortParamValue),
      width: "w-44",
      render: d => (
        <span title={toLocalDateString(d.data.issue_date, true)}>
          {toLocalDateString(d.data.issue_date)}
        </span>
      )
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      sortClicked: createSortFunction("due_date", setSortParamValue),
      width: "w-44",
      render: d => (
        <span title={toLocalDateString(d.data.due_date, true)}>
          {toLocalDateString(d.data.due_date)}
        </span>
      )
    },
    {
      title: "Email Status",
      dataIndex: "email",
      key: "email",
      render: d => (
        <Tag color={d.data.invoice_emails_enabled ? "lime" : "gray"}>
          {d.data.invoice_emails_enabled ? "Enabled" : "Disabled"}
        </Tag>
      )
    },
    {
      title: "Auto Collect",
      dataIndex: "autoCollect",
      key: "autoCollect",
      render: d => <Tag color={d.autoCollect === "Disabled" ? "gray" : "lime"}>{d.autoCollect}</Tag>
    },
    {
      title: "Scheduled Date",
      dataIndex: "scheduledDate",
      key: "scheduledDate",
      render: d => (
        <div>
          {d.data.auto_collection_scheduled_date &&
          d.data.payments_invoice_auto_collection_status ===
            PaymentsInvoiceAutoCollectionStatus.Pending
            ? toLocalDateString(d.data.auto_collection_scheduled_date)
            : "--"}
        </div>
      )
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: d => {
        const paymentMethodDisplayAttributes =
          d.data.payment_method && getPaymentMethodDetails(d.data.payment_method);
        return paymentMethodDisplayAttributes ? (
          <div className="flex items-center pr-4">
            <img
              src={paymentMethodDisplayAttributes.logo}
              alt={paymentMethodDisplayAttributes.name}
              className="w-8 h-8"
            />
            <div>{paymentMethodDisplayAttributes?.name}</div>
          </div>
        ) : (
          "--"
        );
      }
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: d => (
        <InvoiceListActions
          invoice={d.data}
          open={openMoreMenu === d.data.id}
          onClose={() => setOpenMoreMenu(-1)}
          onOpen={() => setOpenMoreMenu(d.data.id)}
        />
      ),
      align: "center",
      width: "w-12"
    }
  ];

  const { getAccessTokenSilently } = useAuth0();

  const refreshThings = useCallback(async () => {
    await Promise.all([
      getInvoices(
        getAccessTokenSilently,
        limit,
        sortParamValue,
        page,
        `collectible_invoice_customer_id:eq:${customerID}`
      ),
      getAdyenKYCDetails(getAccessTokenSilently)
    ]);
  }, [
    customerID,
    getInvoices,
    getAdyenKYCDetails,
    getAccessTokenSilently,
    limit,
    sortParamValue,
    page
  ]);

  useEffect(() => {
    refreshThings();
  }, [refreshThings, limit, sortParamValue, page]);

  const dataSource: DataSourceItem<Invoice>[] = invoices.map(invoice => ({
    key: invoice.id.toString(),
    invoice: invoice.invoice_number,
    email: invoice.invoice_emails_enabled ? "Enabled" : "Disabled",
    autoCollect:
      invoice.payments_invoice_auto_collection_status ===
      PaymentsInvoiceAutoCollectionStatus.Pending
        ? "Enabled"
        : "Disabled",
    dateIssued: toLocalDateString(invoice.issue_date),
    dueDate: toLocalDateString(invoice.due_date),
    total: formatCurrency(invoice.total),
    surcharge_amount: formatCurrency(invoice.surcharge_amount),
    amount_paid: formatCurrency(invoice.amount_paid),
    balance: formatCurrency(invoice.balance),
    data: invoice
  }));

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)]">
      <div className="flex flex-grow flex-col overflow-y-auto">
        {isLoading && invoices.length === 0 ? (
          <LoadingPage />
        ) : (
          <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
        )}
        {!isLoading && invoices.length === 0 && (
          <div className="text-center text-gray-400 mt-12">No Invoices Found</div>
        )}
      </div>
      <TablePaginationFooter
        limit={limit}
        setLimit={setLimit}
        setPage={setPage}
        totalRecords={invoicesCount}
        page={page}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CustomerDetailInvoiceList;
